// §4.10 What-if sandbox, the result (`/planning/scenarios/$scenarioId`, P3-J
// J-4, D-471, D-472, D-474). While `open` a skeleton with the elapsed time,
// re-reading row 82 every two seconds; when `failed` the error in words with
// Solve again; when `solved` the sandbox's lanes, read from row 61 on the
// sandbox id and drawn by the board's lanes read-only (never row 65), the KPI
// cards, utilization by work centre, the cost card for a holder of
// `planning.cost.read` with "Late penalties are not priced", and the slipped
// and recovered orders. A superseded base reads "Based on version N; the plant
// has moved on" with Solve again. Promote (row 84) shows to holders of the
// three codes and confirms exactly what it writes; Discard (row 85) and
// Duplicate (row 80 with the same hypotheses). A phone reads the result.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { Banner, toast } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { PlanningHeader } from "../components/planning-header";
import {
  CostCard,
  DiscardDialog,
  HypothesisList,
  KpiCards,
  OrderShiftTable,
  PromoteDialog,
  ScenarioStatusChip,
  UtilizationTable,
  type HypothesisNames,
} from "../components/scenario-bits";
import { ScheduleCards, ScheduleHatchDefs, ScheduleLanes } from "../components/schedule-lanes";
import { useCalendarWorkCenters } from "../hooks/calendar-queries";
import { useMemberNames } from "../hooks/mps-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { useProductIndex } from "../hooks/planning-queries";
import {
  createScenario,
  discardScenario,
  promoteScenario,
  solveScenario,
  useSandboxBoard,
  useScenario,
  useScenarioRefresh,
  type Scenario,
} from "../hooks/scenario-queries";
import type { ScheduleOperation } from "../hooks/schedule-queries";
import { workCenterLabel } from "../lib/calendar-lib";
import { shortId } from "../lib/mrp-lib";
import { formatDate, useOffline } from "../lib/planning-lib";
import {
  PROMOTE_CODES,
  codeNameKey,
  copyName,
  elapsedText,
  hasPersistable,
  hypothesisInputOf,
  scenarioProblem,
  validateScenarioResultSearch,
  whatIfOrderNumber,
} from "../lib/scenario-lib";
import { boardStart, todayAt, windowOf, zoneOffset, zonedStamp } from "../lib/schedule-lib";
import { usePlanningFacility } from "../planning-layout";

type Dialog = "promote" | "discard" | undefined;

export function PlanningScenarioResultScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const refresh = useScenarioRefresh();
  const zone = facility.timezone;
  const lang = i18n.language;
  const params: Record<string, string | undefined> = useParams({ strict: false });
  const id = params["scenarioId"] ?? "";
  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validateScenarioResultSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );

  const scenario = useScenario(id);
  const data = scenario.data;
  const members = useMemberNames(perms.canReadMembers);
  const workCenters = useCalendarWorkCenters(facility.id);
  const wcName = useMemo(() => {
    const byId = new Map((workCenters.data ?? []).map((wc) => [wc.id, wc]));
    return (wcId: string) => {
      const wc = byId.get(wcId);
      return wc !== undefined ? workCenterLabel(wc, lang) : shortId(wcId);
    };
  }, [workCenters.data, lang]);
  const products = useProductIndex(
    (data?.hypothesis ?? []).flatMap((h) => (h.kind === "add_order" ? [h.product_id] : [])),
  );
  const names: HypothesisNames = {
    workCenter: wcName,
    product: (pid) => products.byId.get(pid),
  };

  const [dialog, setDialog] = useState<Dialog>(undefined);
  const [busy, setBusy] = useState<"solve" | "promote" | "discard" | "duplicate" | undefined>();
  const [dialogError, setDialogError] = useState<string | undefined>(undefined);

  // The solving timer counts from when this view first saw the scenario open.
  const [openSince, setOpenSince] = useState<number | undefined>(undefined);
  const [now, setNow] = useState(Date.now());
  const isOpen = data?.status === "open";
  useEffect(() => {
    if (!isOpen) {
      setOpenSince(undefined);
      return;
    }
    setOpenSince((prev) => prev ?? Date.now());
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [isOpen]);

  const authError = scenario.error instanceof UnauthorizedError ? scenario.error : undefined;
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const backTo = { label: t("planning.scenarios.result.back"), to: "/planning/scenarios" };

  const problemText = (err: unknown): string => {
    const p = scenarioProblem(err);
    const values: Record<string, string | number> = { ...p.values };
    if (typeof values["codes"] === "string") {
      values["codes"] = values["codes"]
        .split(",")
        .map((c) => {
          const key = codeNameKey(c);
          return key !== undefined ? t(`planning.scenarios.codeName.${key}`) : c;
        })
        .join(", ");
    }
    return t(`planning.scenarios.problem.${p.key}`, values);
  };

  const run = async (
    kind: "solve" | "promote" | "discard" | "duplicate",
    action: () => Promise<string | undefined>,
    done: string,
  ) => {
    setBusy(kind);
    setDialogError(undefined);
    try {
      const nextId = await action();
      await refresh(facility.id, [id]);
      toast.success(done);
      setDialog(undefined);
      if (nextId !== undefined) {
        void navigate({ to: "/planning/scenarios/$scenarioId", params: { scenarioId: nextId } });
      }
    } catch (err) {
      const text = problemText(err);
      if (dialog !== undefined) setDialogError(text);
      else toast.error(text);
    } finally {
      setBusy(undefined);
    }
  };

  if (scenario.isPending) {
    return (
      <section className="planning-screen" data-testid="planning-scenario-result">
        <PlanningHeader title={t("planning.scenarios.title")} backTo={backTo} />
        <div data-testid="planning-scenario-result-loading">
          <CardSkeleton label={t("planning.scenarios.result.loading")} />
        </div>
      </section>
    );
  }
  if (data === undefined) {
    const notFound = scenario.error instanceof RequestError && scenario.error.httpStatus === 404;
    return (
      <section className="planning-screen" data-testid="planning-scenario-result">
        <PlanningHeader title={t("planning.scenarios.title")} backTo={backTo} />
        {notFound ? (
          <EmptyState
            title={t("planning.scenarios.result.notFoundTitle")}
            body={t("planning.scenarios.result.notFoundBody")}
            ctaLabel={t("planning.scenarios.result.back")}
            onCta={() => {
              void navigate({ to: "/planning/scenarios" });
            }}
          />
        ) : (
          <ErrorState
            title={t("planning.scenarios.result.loadError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void scenario.refetch()}
            requestId={requestIdOf(scenario.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        )}
      </section>
    );
  }

  const canPromoteCodes = PROMOTE_CODES.every((c) => perms.has(c));
  const persistable = hasPersistable(data.hypothesis);
  const live = data.status === "open" || data.status === "solved" || data.status === "failed";
  const canSolveAgain =
    data.status === "failed" || (data.status === "solved" && data.base_superseded);
  const acting = busy !== undefined || offline;

  const solveAgain = () =>
    void run(
      "solve",
      async () => {
        await solveScenario(data.id);
        return undefined;
      },
      t("planning.scenarios.result.done.solve"),
    );

  const duplicate = () =>
    void run(
      "duplicate",
      async () => {
        const accepted = await createScenario({
          facility_id: data.facility_id,
          name: copyName(data.name, (n) => t("planning.scenarios.result.copyName", { name: n })),
          hypothesis: data.hypothesis.map(hypothesisInputOf),
        });
        return accepted.scenario_id;
      },
      t("planning.scenarios.result.done.duplicate"),
    );

  const actions = desktop ? (
    <>
      {canSolveAgain ? (
        <Button disabled={acting} data-testid="planning-scenario-solve" onClick={solveAgain}>
          {t("planning.scenarios.result.solveAgain")}
        </Button>
      ) : null}
      <Button disabled={acting} data-testid="planning-scenario-duplicate" onClick={duplicate}>
        {t("planning.scenarios.result.duplicate")}
      </Button>
      {live ? (
        <Button
          disabled={acting}
          data-testid="planning-scenario-discard"
          onClick={() => {
            setDialogError(undefined);
            setDialog("discard");
          }}
        >
          {t("planning.scenarios.result.discard")}
        </Button>
      ) : null}
      {data.status === "solved" && canPromoteCodes ? (
        <Button
          variant="primary"
          disabled={acting || !persistable}
          title={persistable ? undefined : t("planning.scenarios.result.promoteTip")}
          data-testid="planning-scenario-promote"
          data-persistable={String(persistable)}
          onClick={() => {
            setDialogError(undefined);
            setDialog("promote");
          }}
        >
          {t("planning.scenarios.result.promote")}
        </Button>
      ) : null}
    </>
  ) : undefined;

  return (
    <section
      className="planning-screen"
      data-testid="planning-scenario-result"
      data-status={data.status}
      data-scenario-id={data.id}
    >
      <PlanningHeader title={data.name} backTo={backTo} actions={actions} />
      <div className="planning-actions">
        <ScenarioStatusChip status={data.status} />
        <span
          className={data.base_superseded ? "planning-warn" : "planning-hint"}
          data-testid="planning-scenario-base-line"
          data-superseded={String(data.base_superseded)}
        >
          {data.base_superseded
            ? t("planning.scenarios.result.superseded", { version: data.base_version })
            : t("planning.scenarios.result.base", { version: data.base_version })}
        </span>
      </div>
      {!desktop ? (
        <p className="planning-note" data-testid="planning-scenario-phone-note">
          {t("planning.scenarios.result.phoneNote")}
        </p>
      ) : null}
      {data.status === "promoted" ? <PromotedLine scenario={data} members={members.byId} /> : null}
      {data.status === "discarded" && data.discarded_at !== null ? (
        <p className="planning-hint" data-testid="planning-scenario-discarded-line">
          {t("planning.scenarios.result.discarded", {
            time: zonedStamp(data.discarded_at, lang, zone),
          })}
        </p>
      ) : null}

      <Card>
        <Card.Body>
          <h2 className="planning-subhead">{t("planning.scenarios.result.changes")}</h2>
          <HypothesisList hypotheses={data.hypothesis} names={names} timezone={zone} />
        </Card.Body>
      </Card>

      {data.status === "open" ? (
        <div data-testid="planning-scenario-solving" role="status">
          <p className="planning-hint" data-testid="planning-scenario-solving-elapsed">
            {t("planning.scenarios.result.solving", {
              elapsed: elapsedText(new Date(openSince ?? now).toISOString(), now),
            })}
          </p>
          <p className="planning-note">{t("planning.scenarios.result.solvingBody")}</p>
          <CardSkeleton label={t("planning.scenarios.result.loading")} lines={6} />
        </div>
      ) : null}

      {data.status === "failed" ? (
        <div data-testid="planning-scenario-failed">
          {desktop ? (
            <ErrorState
              title={t("planning.scenarios.result.failedTitle")}
              detail={t(`planning.scenarios.error.${data.error ?? "COMPUTE_FAILED"}`)}
              retryLabel={t("planning.scenarios.result.solveAgain")}
              onRetry={solveAgain}
            />
          ) : (
            <div className="state state-error" role="alert">
              <p className="state-title">{t("planning.scenarios.result.failedTitle")}</p>
              <p className="state-detail">
                {t(`planning.scenarios.error.${data.error ?? "COMPUTE_FAILED"}`)}
              </p>
            </div>
          )}
        </div>
      ) : null}

      {data.kpi_diff !== null && data.status !== "open" && data.status !== "failed" ? (
        <ResultBody scenario={data} names={names} desktop={desktop} from={search.from} />
      ) : null}

      {dialog === "promote" ? (
        <PromoteDialog
          hypotheses={data.hypothesis}
          names={names}
          timezone={zone}
          pending={busy === "promote"}
          error={dialogError}
          onClose={() => {
            setDialog(undefined);
          }}
          onConfirm={() =>
            void run(
              "promote",
              async () => {
                await promoteScenario(data.id);
                return undefined;
              },
              t("planning.scenarios.result.done.promote"),
            )
          }
        />
      ) : null}
      {dialog === "discard" ? (
        <DiscardDialog
          pending={busy === "discard"}
          error={dialogError}
          onClose={() => {
            setDialog(undefined);
          }}
          onConfirm={() =>
            void run(
              "discard",
              async () => {
                await discardScenario(data.id);
                return undefined;
              },
              t("planning.scenarios.result.done.discard"),
            )
          }
        />
      ) : null}
    </section>
  );
}

function PromotedLine({
  scenario,
  members,
}: {
  scenario: Scenario;
  members: ReadonlyMap<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const { facility } = usePlanningFacility();
  const applied = scenario.applied_changes;
  return (
    <div data-testid="planning-scenario-promoted-line">
      {scenario.promoted_at !== null ? (
        <p className="planning-hint">
          {t("planning.scenarios.result.promoted", {
            who:
              scenario.promoted_by === null
                ? ""
                : (members.get(scenario.promoted_by) ?? shortId(scenario.promoted_by)),
            time: zonedStamp(scenario.promoted_at, i18n.language, facility.timezone),
          })}
        </p>
      ) : null}
      {applied !== null ? (
        <p
          className="planning-hint"
          data-testid="planning-scenario-applied"
          data-exceptions={String(applied.exception_ids.length)}
          data-patterns={String(applied.pattern_ids.length)}
        >
          {t("planning.scenarios.result.applied", {
            shifts: applied.exception_ids.length,
            patterns: applied.pattern_ids.length,
          })}
        </p>
      ) : null}
    </div>
  );
}

/** A sandbox operation with a what-if order's label in words, not its code. */
function withWhatIfLabel(op: ScheduleOperation, t: TFunction): ScheduleOperation {
  if (op.source_kind !== "scenario_order_op") return op;
  const n = whatIfOrderNumber(op.label.order_no);
  return n === undefined
    ? op
    : {
        ...op,
        label: { ...op.label, order_no: t("planning.scenarios.result.whatIfOrder", { n }) },
      };
}

function ResultBody({
  scenario,
  names,
  desktop,
  from,
}: {
  scenario: Scenario;
  names: HypothesisNames;
  desktop: boolean;
  from: string | undefined;
}) {
  const { t } = useTranslation();
  const diff = scenario.kpi_diff;
  const { facility } = usePlanningFacility();
  const zone = facility.timezone;
  if (diff === null) return null;
  return (
    <>
      <h2 className="planning-subhead">{t("planning.scenarios.result.kpis")}</h2>
      <KpiCards diff={diff} />
      {diff.cost_impact !== undefined ? <CostCard cost={diff.cost_impact} /> : null}
      <Card>
        <Card.Body>
          <h2 className="planning-subhead">{t("planning.scenarios.result.utilization")}</h2>
          <UtilizationTable rows={diff.utilization_delta} workCenter={names.workCenter} />
        </Card.Body>
      </Card>
      {scenario.sandbox_schedule_id !== null ? (
        <SandboxLanes
          scenarioId={scenario.id}
          sandboxId={scenario.sandbox_schedule_id}
          desktop={desktop}
          from={from}
        />
      ) : null}
      <OrderShiftTable
        kind="slipped"
        rows={diff.slipped_orders}
        count={diff.slipped_count}
        timezone={zone}
      />
      <OrderShiftTable
        kind="recovered"
        rows={diff.recovered_orders}
        count={diff.recovered_count}
        timezone={zone}
      />
    </>
  );
}

/**
 * The sandbox's lanes (row 61 on the sandbox id), drawn by the board's lanes
 * read-only over a week inside the sandbox's horizon, with Earlier and Later;
 * a phone reads one lane's cards.
 */
function SandboxLanes({
  scenarioId,
  sandboxId,
  desktop,
  from: wanted,
}: {
  scenarioId: string;
  sandboxId: string;
  desktop: boolean;
  from: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const zone = facility.timezone;
  const days = desktop ? 7 : 1;
  const today = todayAt(zone);
  const [horizon, setHorizon] = useState<{ start: string; days: number } | undefined>(undefined);
  const from = boardStart(wanted, today, horizon, days);
  const window = useMemo(() => windowOf(from, days, zone), [from, days, zone]);
  const board = useSandboxBoard(sandboxId, { from: window.from, to: window.to });
  const header = board.data?.schedule ?? null;
  useEffect(() => {
    if (header === null) return;
    setHorizon((prev) =>
      prev?.start === header.horizon_start && prev.days === header.horizon_days
        ? prev
        : { start: header.horizon_start, days: header.horizon_days },
    );
  }, [header]);
  const [laneId, setLaneId] = useState<string | undefined>(undefined);

  const lanes = board.data?.lanes ?? [];
  const ops = useMemo(
    () => (board.data?.operations ?? []).map((op) => withWhatIfLabel(op, t)),
    [board.data, t],
  );
  const unplaced = useMemo(
    () => (board.data?.unplaced ?? []).map((op) => withWhatIfLabel(op, t)),
    [board.data, t],
  );
  const move = (delta: number) => {
    void navigate({
      to: "/planning/scenarios/$scenarioId",
      params: { scenarioId },
      search: { from: addDaysIso(from, delta) },
      replace: true,
    });
  };
  const atStart = horizon !== undefined && from <= horizon.start;
  const atEnd =
    horizon !== undefined && addDaysIso(from, days) > addDaysIso(horizon.start, horizon.days - 1);

  return (
    <Card>
      <Card.Body>
        <div className="planning-actions" data-testid="planning-scenario-lanes-head">
          <h2 className="planning-subhead">{t("planning.scenarios.result.lanes")}</h2>
          <span className="planning-hint" data-testid="planning-scenario-lanes-window">
            {t("planning.scenarios.result.window", {
              from: formatDate(window.from, i18n.language),
              to: formatDate(window.to, i18n.language),
            })}
          </span>
          <Button
            size="sm"
            disabled={atStart}
            data-testid="planning-scenario-lanes-earlier"
            onClick={() => {
              move(-days);
            }}
          >
            {t("planning.scenarios.result.earlier")}
          </Button>
          <Button
            size="sm"
            disabled={atEnd}
            data-testid="planning-scenario-lanes-later"
            onClick={() => {
              move(days);
            }}
          >
            {t("planning.scenarios.result.later")}
          </Button>
        </div>
        {board.isPending ? (
          <div data-testid="planning-scenario-lanes-loading">
            <CardSkeleton label={t("planning.scenarios.result.lanesLoading")} lines={5} />
          </div>
        ) : board.error !== null && board.data === undefined ? (
          <ErrorState
            title={t("planning.scenarios.result.lanesError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void board.refetch()}
            requestId={requestIdOf(board.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        ) : ops.length === 0 && unplaced.length === 0 ? (
          <p className="planning-note" data-testid="planning-scenario-lanes-none">
            {t("planning.scenarios.result.lanesNone")}
          </p>
        ) : desktop ? (
          <div data-testid="planning-scenario-lanes">
            <ScheduleLanes
              lanes={lanes}
              operations={ops}
              unplaced={unplaced}
              overloads={[]}
              window={window}
              timezone={zone}
              zoom="week"
              onOpen={() => undefined}
              onOverload={() => undefined}
            />
          </div>
        ) : (
          <div data-testid="planning-scenario-lanes">
            <Field label={t("planning.scenarios.result.lane")}>
              <Select
                value={laneId ?? lanes[0]?.work_center_id ?? ""}
                data-testid="planning-scenario-lanes-lane"
                onChange={(e) => {
                  setLaneId(e.target.value);
                }}
              >
                {lanes.map((l) => (
                  <option key={l.work_center_id} value={l.work_center_id}>
                    {l.name.length > 0 && l.name !== l.code ? `${l.code} · ${l.name}` : l.code}
                  </option>
                ))}
              </Select>
            </Field>
            <ScheduleHatchDefs />
            <ScheduleCards
              ops={ops.filter((o) => o.work_center_id === (laneId ?? lanes[0]?.work_center_id))}
              unplaced={unplaced.filter(
                (o) => o.work_center_id === (laneId ?? lanes[0]?.work_center_id),
              )}
              timezone={zone}
              onOpen={() => undefined}
            />
          </div>
        )}
        <p className="planning-hint">
          {t("planning.scenarios.result.zoneLine", { offset: zoneOffset(window.startMs, zone) })}
        </p>
        {board.data !== undefined && board.data.unplaced_count > unplaced.length ? (
          <Banner kind="neutral">
            {t("planning.schedule.unplacedMore", {
              count: board.data.unplaced_count - unplaced.length,
            })}
          </Banner>
        ) : null}
      </Card.Body>
    </Card>
  );
}
